/**
 * MapMeasureEditor — Editor pe hartă cu Leaflet + Esri World Imagery + OSM.
 *
 * FREE — nu necesită cheie API sau billing:
 *  - Fundal satelit: Esri World Imagery (attribution required)
 *  - Fundal stradă: OpenStreetMap
 *  - Geocoder: Nominatim (rate-limit 1req/s, respectă politica lor)
 *  - Calcul arie: formulă geodezică sferică (elipsoid WGS84 aproximat sferă R=6378137m)
 */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { parseGeoFile } from "@/utils/geoParsers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Search, MapPin, Plus, Trash2, Save, Download, ArrowLeft, Ruler,
  CornerDownRight, Minus, Upload, Satellite, Map as MapIcon2, Loader2,
} from "lucide-react";

const PALETTE = ["#0d9488", "#dc2626", "#2563eb", "#f59e0b", "#7c3aed", "#059669"];
const newPolygonName = (i) => (i === 0 ? "Suprafață principală" : `Suprafață extra #${i}`);

// ---- Geodesic math (WGS84 sphere approx R=6378137m) ----
const EARTH_R = 6378137;
const toRad = (d) => (d * Math.PI) / 180;

/** Geodesic spherical polygon area in m² (L'Huilier via signed spherical excess). */
function geodesicArea(points) {
  if (points.length < 3) return 0;
  let total = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    total += (toRad(p2.lng) - toRad(p1.lng)) *
      (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
  }
  return Math.abs(total * EARTH_R * EARTH_R / 2);
}

/** Haversine distance between 2 lat/lng points in meters. */
function haversine(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(s));
}

/** Perimeter of a closed polygon in meters. */
function geodesicPerimeter(points) {
  if (points.length < 2) return 0;
  let d = 0;
  const closed = points.length >= 3 ? [...points, points[0]] : points;
  for (let i = 0; i < closed.length - 1; i++) {
    d += haversine(closed[i], closed[i + 1]);
  }
  return d;
}

// ---- Debounce helper ----
function useDebouncedValue(value, delay) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function MapMeasureEditor({
  companyId, measurement, onBack, onSaved, onExport,
}) {
  const mapWrapRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null); // tile layer
  const drawnLayerGroupRef = useRef(null); // FeatureGroup with polygons
  const markerRef = useRef(null);
  const gPolyRefs = useRef({}); // id -> L.Polygon
  const gVertexRefs = useRef({}); // id -> L.Marker[] draggable vertices

  const [name, setName] = useState(measurement?.name || "Măsurătoare nouă");
  const [address, setAddress] = useState(measurement?.address || "");
  const [tileMode, setTileMode] = useState("satellite"); // 'satellite' | 'street'
  const [center] = useState(() => ({
    lat: measurement?.gps_lat ?? 45.9432,
    lng: measurement?.gps_lng ?? 24.9668,
  }));
  const [zoom, setZoom] = useState(measurement?.map_zoom || 6);

  const [polygons, setPolygons] = useState(() => (
    (measurement?.polygons || []).map((p, i) => ({
      id: p.id || `poly_${Date.now()}_${i}`,
      name: p.name || newPolygonName(i),
      color: p.color || PALETTE[i % PALETTE.length],
      role: p.role === "subtract" ? "subtract" : "add",
      points: p.points || [],
      area_m2: p.area_m2 || 0,
      perimeter_m: p.perimeter_m || 0,
    }))
  ));
  const [activePolyId, setActivePolyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const activePolyIdRef = useRef(null);
  useEffect(() => { activePolyIdRef.current = activePolyId; }, [activePolyId]);

  // ---- Search state (Nominatim) ----
  const [searchInput, setSearchInput] = useState(address);
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(searchInput, 400);

  // ---- Init map ----
  useEffect(() => {
    if (!mapWrapRef.current || mapRef.current) return;
    const map = L.map(mapWrapRef.current, {
      center: [center.lat, center.lng],
      zoom,
      zoomControl: true,
      maxZoom: 22,
    });
    mapRef.current = map;
    // Feature group for drawn shapes
    const fg = L.featureGroup().addTo(map);
    drawnLayerGroupRef.current = fg;

    // Initial tile layer
    const satLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution:
          "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        maxZoom: 20,
      },
    );
    satLayer.addTo(map);
    layerRef.current = satLayer;

    // Click to add point
    map.on("click", (e) => handleMapClick(e.latlng));

    // Re-render existing polygons
    polygons.forEach((p) => renderPolygon(p));
    if (polygons.length && polygons[0].points.length >= 2) {
      const bounds = L.latLngBounds(polygons.flatMap((p) => p.points.map(pt => [pt.lat, pt.lng])));
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30] });
    }
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  // Swap tile layer when mode changes
  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    mapRef.current.removeLayer(layerRef.current);
    let l;
    if (tileMode === "satellite") {
      l = L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { attribution: "Tiles &copy; Esri", maxZoom: 20 },
      );
    } else {
      l = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      });
    }
    l.addTo(mapRef.current);
    layerRef.current = l;
  }, [tileMode]);

  // ---------- Polygon logic ----------
  const activePoly = useMemo(() => polygons.find((p) => p.id === activePolyId) || null, [polygons, activePolyId]);

  const computeMetrics = (points) => ({
    area: geodesicArea(points),
    perim: geodesicPerimeter(points),
  });

  const clearVertexMarkers = (polyId) => {
    (gVertexRefs.current[polyId] || []).forEach((m) => m.remove());
    gVertexRefs.current[polyId] = [];
  };

  const renderVertexMarkers = (poly) => {
    if (!mapRef.current) return;
    clearVertexMarkers(poly.id);
    const isSub = poly.role === "subtract";
    const color = isSub ? "#dc2626" : poly.color;
    poly.points.forEach((pt, idx) => {
      const icon = L.divIcon({
        className: "poly-vertex",
        html: `<div style="width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.5)"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      const m = L.marker([pt.lat, pt.lng], { icon, draggable: true }).addTo(mapRef.current);
      m.on("drag", (ev) => {
        const ll = ev.target.getLatLng();
        setPolygons((prev) => prev.map((pp) => {
          if (pp.id !== poly.id) return pp;
          const pts = pp.points.map((old, i) => (i === idx ? { lat: ll.lat, lng: ll.lng } : old));
          const met = computeMetrics(pts);
          return { ...pp, points: pts, area_m2: met.area, perimeter_m: met.perim };
        }));
      });
      m.on("contextmenu", (ev) => {
        L.DomEvent.stopPropagation(ev);
        setPolygons((prev) => prev.map((pp) => {
          if (pp.id !== poly.id) return pp;
          const pts = pp.points.filter((_, i) => i !== idx);
          const met = computeMetrics(pts);
          return { ...pp, points: pts, area_m2: met.area, perimeter_m: met.perim };
        }));
      });
      gVertexRefs.current[poly.id] = (gVertexRefs.current[poly.id] || []).concat(m);
    });
  };

  const renderPolygon = (poly) => {
    if (!mapRef.current || !drawnLayerGroupRef.current) return;
    // Remove existing google polygon for this id if any
    if (gPolyRefs.current[poly.id]) {
      drawnLayerGroupRef.current.removeLayer(gPolyRefs.current[poly.id]);
    }
    const latlngs = poly.points.map((p) => [p.lat, p.lng]);
    const isSub = poly.role === "subtract";
    const pane = drawnLayerGroupRef.current;
    let shape;
    if (poly.points.length >= 3) {
      shape = L.polygon(latlngs, {
        color: isSub ? "#dc2626" : poly.color,
        weight: isSub ? 3 : 2.5,
        opacity: 1,
        fillColor: isSub ? "#dc2626" : poly.color,
        fillOpacity: isSub ? 0.15 : 0.28,
        dashArray: isSub ? "6 4" : null,
      });
    } else if (poly.points.length >= 2) {
      shape = L.polyline(latlngs, {
        color: isSub ? "#dc2626" : poly.color,
        weight: 2.5,
        dashArray: isSub ? "6 4" : null,
      });
    } else {
      // Single point — skip drawing (vertex marker handles it)
      renderVertexMarkers(poly);
      return;
    }
    shape.on("click", (e) => { L.DomEvent.stopPropagation(e); setActivePolyId(poly.id); });
    pane.addLayer(shape);
    gPolyRefs.current[poly.id] = shape;
    renderVertexMarkers(poly);
  };

  // Re-render polygons whenever they change
  useEffect(() => {
    if (!mapRef.current) return;
    const ids = new Set(polygons.map((p) => p.id));
    Object.keys(gPolyRefs.current).forEach((id) => {
      if (!ids.has(id)) {
        if (gPolyRefs.current[id]) drawnLayerGroupRef.current.removeLayer(gPolyRefs.current[id]);
        delete gPolyRefs.current[id];
        clearVertexMarkers(id);
      }
    });
    polygons.forEach((p) => renderPolygon(p));
  }, [polygons]);

  const handleMapClick = (latlng) => {
    const aid = activePolyIdRef.current;
    if (!aid) {
      const p = {
        id: `poly_${Date.now()}`,
        name: newPolygonName(polygons.length),
        color: PALETTE[polygons.length % PALETTE.length],
        role: "add",
        points: [{ lat: latlng.lat, lng: latlng.lng }],
        area_m2: 0,
        perimeter_m: 0,
      };
      setPolygons((prev) => [...prev, p]);
      setActivePolyId(p.id);
      return;
    }
    setPolygons((prev) => prev.map((pp) => {
      if (pp.id !== aid) return pp;
      const nextPoints = [...pp.points, { lat: latlng.lat, lng: latlng.lng }];
      const m = computeMetrics(nextPoints);
      return { ...pp, points: nextPoints, area_m2: m.area, perimeter_m: m.perim };
    }));
  };

  const handleAddSurface = (role = "add") => {
    const p = {
      id: `poly_${Date.now()}`,
      name: role === "subtract"
        ? `Suprafață scăzută #${polygons.filter(x => x.role === "subtract").length + 1}`
        : newPolygonName(polygons.filter(x => x.role !== "subtract").length),
      color: PALETTE[polygons.length % PALETTE.length],
      role,
      points: [],
      area_m2: 0,
      perimeter_m: 0,
    };
    setPolygons((prev) => [...prev, p]);
    setActivePolyId(p.id);
    toast.info(role === "subtract"
      ? "Click pe hartă pentru zona ce se scade (ex: luminator)."
      : "Click pe hartă pentru a adăuga puncte.");
  };

  const handleUndoLastPoint = () => {
    if (!activePoly || activePoly.points.length === 0) return;
    setPolygons((prev) => prev.map((pp) => {
      if (pp.id !== activePolyId) return pp;
      const nextPoints = pp.points.slice(0, -1);
      const m = computeMetrics(nextPoints);
      return { ...pp, points: nextPoints, area_m2: m.area, perimeter_m: m.perim };
    }));
  };

  const handleDeletePolygon = (id) => {
    setPolygons((prev) => prev.filter((p) => p.id !== id));
    if (activePolyId === id) setActivePolyId(null);
  };

  const handleClearActive = () => {
    if (!activePoly) return;
    if (!window.confirm("Ștergi toate punctele acestei suprafețe?")) return;
    setPolygons((prev) => prev.map((pp) => (
      pp.id === activePolyId ? { ...pp, points: [], area_m2: 0, perimeter_m: 0 } : pp
    )));
  };

  const handleRename = (id, val) => {
    setPolygons((prev) => prev.map((p) => (p.id === id ? { ...p, name: val } : p)));
  };

  const handleColor = (id, color) => {
    setPolygons((prev) => prev.map((p) => (p.id === id ? { ...p, color } : p)));
  };

  const handleImportGeo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const parsed = await parseGeoFile(file);
      if (!parsed.length) { toast.error("Nu am găsit poligoane în fișier."); return; }
      const newPolys = parsed.map((p, i) => {
        const m = computeMetrics(p.points);
        return {
          id: `poly_${Date.now()}_${i}`,
          name: p.name || newPolygonName(polygons.length + i),
          color: PALETTE[(polygons.length + i) % PALETTE.length],
          role: "add",
          points: p.points,
          area_m2: m.area,
          perimeter_m: m.perim,
        };
      });
      setPolygons((prev) => [...prev, ...newPolys]);
      if (mapRef.current && newPolys[0]?.points?.length) {
        const bounds = L.latLngBounds(newPolys.flatMap(np => np.points.map(pt => [pt.lat, pt.lng])));
        mapRef.current.fitBounds(bounds, { padding: [30, 30] });
      }
      toast.success(`Import reușit: ${newPolys.length} poligon(e).`);
    } catch (err) {
      console.error(err);
      toast.error(`Import eșuat: ${err.message || err}`);
    }
  };

  // ---------- Search (Nominatim) ----------
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 3) { setSearchResults([]); return; }
    let cancel = false;
    setSearchLoading(true);
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=5&accept-language=ro&countrycodes=ro&q=${encodeURIComponent(debouncedSearch)}`,
      { headers: { "Accept": "application/json" } },
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancel) return;
        setSearchResults(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (cancel) return;
        setSearchResults([]);
      })
      .finally(() => { if (!cancel) setSearchLoading(false); });
    return () => { cancel = true; };
  }, [debouncedSearch]);

  const goToResult = (res) => {
    if (!mapRef.current) return;
    const lat = parseFloat(res.lat);
    const lon = parseFloat(res.lon);
    mapRef.current.setView([lat, lon], 20);
    setAddress(res.display_name || searchInput);
    setSearchInput(res.display_name || searchInput);
    setSearchResults([]);
    setSearchOpen(false);
    if (markerRef.current) markerRef.current.remove();
    markerRef.current = L.circleMarker([lat, lon], {
      radius: 7, color: "#fff", weight: 2, fillColor: "#0d9488", fillOpacity: 1,
    }).addTo(mapRef.current);
  };

  // ---------- Totals ----------
  const totalArea = Math.max(0, polygons.reduce((s, p) => (
    s + (p.role === "subtract" ? -1 : 1) * (p.area_m2 || 0)
  ), 0));
  const totalPerim = polygons.filter(p => p.role !== "subtract")
    .reduce((s, p) => s + (p.perimeter_m || 0), 0);

  // ---------- Save / Export ----------
  const buildPayload = () => {
    const c = mapRef.current?.getCenter?.();
    const currentZoom = mapRef.current?.getZoom?.() || zoom;
    return {
      name,
      mode: "map",
      address,
      gps_lat: c ? c.lat : center.lat,
      gps_lng: c ? c.lng : center.lng,
      map_zoom: currentZoom,
      polygons: polygons.map((p) => ({
        id: p.id, name: p.name, color: p.color, role: p.role || "add",
        points: p.points, area_m2: p.area_m2, perimeter_m: p.perimeter_m,
      })),
      total_area_m2: totalArea,
      total_perimeter_m: totalPerim,
    };
  };

  const performSave = async () => {
    const axios = (await import("axios")).default;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const params = { company_id: companyId };
    const payload = buildPayload();
    if (measurement?.id) {
      const { data } = await axios.patch(`${API}/sketches/measurements/${measurement.id}`, payload, { params });
      return data.measurement;
    }
    const { data } = await axios.post(`${API}/sketches/measurements`, payload, { params });
    return data.measurement;
  };

  const handleSave = async () => {
    if (!polygons.some((p) => p.points.length >= 3)) {
      toast.error("Trebuie cel puțin o suprafață cu 3 puncte pentru a salva.");
      return;
    }
    try {
      setSaving(true);
      const saved = await performSave();
      toast.success("Măsurătoare salvată");
      onSaved && onSaved(saved);
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut salva măsurătoarea");
    } finally { setSaving(false); }
  };

  const handleExport = async () => {
    if (!polygons.some((p) => p.points.length >= 3)) {
      toast.error("Trebuie cel puțin o suprafață cu 3 puncte.");
      return;
    }
    try {
      setSaving(true);
      const saved = await performSave();
      onExport && onExport(saved);
    } catch (e) {
      console.error(e);
      toast.error("Nu am putut pregăti exportul");
    } finally { setSaving(false); }
  };

  // ---------- Render ----------
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="map-measure-editor">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="map-measure-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Înapoi
        </Button>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Denumire măsurătoare"
          className="max-w-xs h-9"
          data-testid="map-measure-name-input"
        />
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            placeholder="Caută adresă (ex: Str. Victoriei 12, București)"
            className="h-9 pl-8"
            data-testid="map-measure-search-input"
          />
          {searchOpen && (searchResults.length > 0 || searchLoading) && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-20 max-h-64 overflow-y-auto">
              {searchLoading && (
                <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Caut…
                </div>
              )}
              {searchResults.map((r, i) => (
                <button
                  key={r.place_id || i}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); goToResult(r); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                >
                  <div className="text-xs font-medium text-slate-800 truncate">{r.display_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setTileMode(tileMode === "satellite" ? "street" : "satellite")}
          title={tileMode === "satellite" ? "Comută pe hartă stradală" : "Comută pe satelit"}
        >
          {tileMode === "satellite" ? <MapIcon2 className="w-4 h-4 mr-1" /> : <Satellite className="w-4 h-4 mr-1" />}
          {tileMode === "satellite" ? "Stradă" : "Satelit"}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="bg-slate-700 hover:bg-slate-800 text-white"
          data-testid="map-measure-save-btn"
        >
          <Save className="w-4 h-4 mr-1" /> Salvează
        </Button>
        <Button
          size="sm"
          onClick={handleExport}
          disabled={saving}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="map-measure-export-btn"
        >
          <Download className="w-4 h-4 mr-1" /> Exportă în DevizePRO
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
        {/* Map */}
        <div className="relative">
          <div ref={mapWrapRef} className="w-full" style={{ height: "70vh", minHeight: 520 }} data-testid="map-measure-canvas" />
          <div className="absolute left-3 bottom-3 bg-white/95 backdrop-blur rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-sm space-y-0.5 z-[400]">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <Ruler className="w-4 h-4 text-teal-600" />
              Total suprafață: <span className="text-teal-700">{totalArea.toFixed(2)} m²</span>
            </div>
            <div className="text-xs text-slate-600">
              Perimetru total: <span className="font-medium">{totalPerim.toFixed(2)} m</span> · {polygons.filter(p => p.role !== "subtract").length} zonă(e)
              {polygons.some(p => p.role === "subtract") && (
                <span className="text-red-600 ml-1">
                  · − {polygons.filter(p => p.role === "subtract").length} scădere(i)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="border-l border-slate-200 bg-slate-50 flex flex-col" style={{ maxHeight: "70vh" }}>
          <div className="p-3 border-b border-slate-200 bg-white flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => handleAddSurface("add")}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="map-measure-add-surface-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Suprafață
            </Button>
            <Button
              size="sm"
              onClick={() => handleAddSurface("subtract")}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="map-measure-add-hole-btn"
            >
              <Minus className="w-4 h-4 mr-1" /> Scăzută
            </Button>
            <label className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-xs">
              <Upload className="w-3.5 h-3.5" /> KML/GeoJSON
              <input
                type="file"
                accept=".kml,.geojson,.json"
                className="hidden"
                onChange={handleImportGeo}
                data-testid="map-measure-import-input"
              />
            </label>
            <Button size="sm" variant="outline" onClick={handleUndoLastPoint} disabled={!activePoly || activePoly.points.length === 0}>
              <CornerDownRight className="w-4 h-4 mr-1" /> Undo
            </Button>
            <Button size="sm" variant="outline" onClick={handleClearActive} disabled={!activePoly}>
              <Trash2 className="w-4 h-4 mr-1" /> Curăță
            </Button>
          </div>
          <div className="p-3 text-xs text-slate-600 border-b border-slate-200 bg-white">
            Click pe hartă = adaugă punct. <br />
            <span className="text-red-600 font-medium">Roșu = suprafețe scăzute</span> (luminatoare, coșuri).
            Trage vârfurile pentru ajustare fină. Click-dreapta pe un vârf = șterge.
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {polygons.length === 0 && (
              <div className="text-center py-10 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                Nicio suprafață. Caută o adresă și click pe hartă.
              </div>
            )}
            {polygons.map((p) => (
              <div
                key={p.id}
                className={`rounded-lg border bg-white p-2.5 cursor-pointer transition ${p.id === activePolyId ? "ring-2 ring-teal-500 border-teal-300" : "border-slate-200 hover:border-slate-300"}`}
                onClick={() => setActivePolyId(p.id)}
                data-testid={`map-measure-poly-${p.id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: p.role === "subtract" ? "#dc2626" : p.color }} />
                  <Input
                    value={p.name}
                    onChange={(e) => handleRename(p.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 text-sm px-2 border-slate-200"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-red-500 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); handleDeletePolygon(p.id); }}
                    data-testid={`map-measure-poly-del-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-600">
                  {p.role === "subtract" && (
                    <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">
                      <Minus className="w-3 h-3 mr-0.5" /> Scădere
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]">
                    Arie: <span className={`font-semibold ml-1 ${p.role === "subtract" ? "text-red-700" : "text-teal-700"}`}>
                      {p.role === "subtract" ? "−" : ""}{p.area_m2.toFixed(2)} m²
                    </span>
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    Perim: <span className="font-semibold text-slate-700 ml-1">{p.perimeter_m.toFixed(2)} m</span>
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">Puncte: {p.points.length}</Badge>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolygons(prev => prev.map(pp => pp.id === p.id ? { ...pp, role: pp.role === "subtract" ? "add" : "subtract" } : pp));
                    }}
                    className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-100"
                    data-testid={`map-measure-poly-role-${p.id}`}
                  >
                    {p.role === "subtract" ? "→ Adaugă" : "→ Scade"}
                  </button>
                </div>
                <div className="flex items-center gap-1 mt-1.5">
                  {PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleColor(p.id, c); }}
                      className={`w-4 h-4 rounded-sm border ${p.color === c ? "border-slate-900 ring-1 ring-slate-900" : "border-white shadow-sm"}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
